import { type RegulatoryAuthorityCode } from '#app/utils/regulatory-authorities.ts'
import { type CountryCode } from '#types/country.ts'

export interface CountryData {
	code: string
	name: string
	flag: string
	dialCode: string
	region: string
	authority: RegulatoryAuthorityCode
	units: 'imperial' | 'metric'
}

// Countries with supported regulatory authorities that map to our RegulatoryAuthorityCode values
export const SUPPORTED_COUNTRIES: CountryData[] = [
	// United States
	{
		code: 'US',
		name: 'United States',
		flag: '🇺🇸',
		dialCode: '+1',
		region: 'North America',
		authority: 'FDA',
		units: 'imperial',
	},

	// Canada
	{
		code: 'CA',
		name: 'Canada',
		flag: '🇨🇦',
		dialCode: '+1',
		region: 'North America',
		authority: 'HEALTH_CANADA',
		units: 'metric',
	},

	// United Kingdom
	{
		code: 'GB',
		name: 'United Kingdom',
		flag: '🇬🇧',
		dialCode: '+44',
		region: 'Europe',
		authority: 'MHRA',
		units: 'metric',
	},

	// Switzerland
	{
		code: 'CH',
		name: 'Switzerland',
		flag: '🇨🇭',
		dialCode: '+41',
		region: 'Europe',
		authority: 'SWISSMEDIC',
		units: 'metric',
	},

	// Australia
	{
		code: 'AU',
		name: 'Australia',
		flag: '🇦🇺',
		dialCode: '+61',
		region: 'Asia-Pacific',
		authority: 'TGA',
		units: 'metric',
	},

	// Japan
	{
		code: 'JP',
		name: 'Japan',
		flag: '🇯🇵',
		dialCode: '+81',
		region: 'Asia-Pacific',
		authority: 'PMDA',
		units: 'metric',
	},

	// Brazil
	{
		code: 'BR',
		name: 'Brazil',
		flag: '🇧🇷',
		dialCode: '+55',
		region: 'South America',
		authority: 'ANVISA',
		units: 'metric',
	},

	// China
	{
		code: 'CN',
		name: 'China',
		flag: '🇨🇳',
		dialCode: '+86',
		region: 'Asia',
		authority: 'NMPA',
		units: 'metric',
	},

	// EU Member States (EMA covers all EU member states)
	{
		code: 'DE',
		name: 'Germany',
		flag: '🇩🇪',
		dialCode: '+49',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'FR',
		name: 'France',
		flag: '🇫🇷',
		dialCode: '+33',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'IT',
		name: 'Italy',
		flag: '🇮🇹',
		dialCode: '+39',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'ES',
		name: 'Spain',
		flag: '🇪🇸',
		dialCode: '+34',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'NL',
		name: 'Netherlands',
		flag: '🇳🇱',
		dialCode: '+31',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'BE',
		name: 'Belgium',
		flag: '🇧🇪',
		dialCode: '+32',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'AT',
		name: 'Austria',
		flag: '🇦🇹',
		dialCode: '+43',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'SE',
		name: 'Sweden',
		flag: '🇸🇪',
		dialCode: '+46',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'DK',
		name: 'Denmark',
		flag: '🇩🇰',
		dialCode: '+45',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'FI',
		name: 'Finland',
		flag: '🇫🇮',
		dialCode: '+358',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'IE',
		name: 'Ireland',
		flag: '🇮🇪',
		dialCode: '+353',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'PT',
		name: 'Portugal',
		flag: '🇵🇹',
		dialCode: '+351',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'PL',
		name: 'Poland',
		flag: '🇵🇱',
		dialCode: '+48',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'CZ',
		name: 'Czech Republic',
		flag: '🇨🇿',
		dialCode: '+420',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'HU',
		name: 'Hungary',
		flag: '🇭🇺',
		dialCode: '+36',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'RO',
		name: 'Romania',
		flag: '🇷🇴',
		dialCode: '+40',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'BG',
		name: 'Bulgaria',
		flag: '🇧🇬',
		dialCode: '+359',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'HR',
		name: 'Croatia',
		flag: '🇭🇷',
		dialCode: '+385',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'SK',
		name: 'Slovakia',
		flag: '🇸🇰',
		dialCode: '+421',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'SI',
		name: 'Slovenia',
		flag: '🇸🇮',
		dialCode: '+386',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'EE',
		name: 'Estonia',
		flag: '🇪🇪',
		dialCode: '+372',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'LV',
		name: 'Latvia',
		flag: '🇱🇻',
		dialCode: '+371',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'LT',
		name: 'Lithuania',
		flag: '🇱🇹',
		dialCode: '+370',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'LU',
		name: 'Luxembourg',
		flag: '🇱🇺',
		dialCode: '+352',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'MT',
		name: 'Malta',
		flag: '🇲🇹',
		dialCode: '+356',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'CY',
		name: 'Cyprus',
		flag: '🇨🇾',
		dialCode: '+357',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
	{
		code: 'GR',
		name: 'Greece',
		flag: '🇬🇷',
		dialCode: '+30',
		region: 'European Union',
		authority: 'EMA',
		units: 'metric',
	},
]

// Group countries by region for better UX
export const getCountriesByRegion = () => {
	const grouped = SUPPORTED_COUNTRIES.reduce<Record<string, CountryData[]>>(
		(acc, country) => {
			const list = acc[country.region] ?? (acc[country.region] = [])
			list.push(country)
			return acc
		},
		{},
	)

	// Sort countries within each region alphabetically, except put US first in North America
	Object.keys(grouped).forEach((region) => {
		if (region === 'North America') {
			// Put United States first, then sort the rest alphabetically
			const us = grouped[region]?.find((country) => country.code === 'US')
			const others = grouped[region]
				?.filter((country) => country.code !== 'US')
				.sort((a, b) => a.name.localeCompare(b.name))
			grouped[region] = us ? [us, ...(others || [])] : others || []
		} else {
			grouped[region]?.sort((a, b) => a.name.localeCompare(b.name))
		}
	})

	return grouped
}

// Utility function to get dial code by country code
export const getCountryByCode = (
	countryCode?: string | CountryCode | null,
): CountryData | undefined => {
	if (!countryCode) return undefined
	const normalized = String(countryCode).toUpperCase()
	return SUPPORTED_COUNTRIES.find((country) => country.code === normalized)
}

export const getDialCodeByCountry = (countryCode: string): string => {
	const country = getCountryByCode(countryCode)
	return country?.dialCode ?? '+1'
}

// Utility function to get unit system by country code
export const getUnitSystemByCountry = (
	countryCode: string,
): 'imperial' | 'metric' => {
	const country = getCountryByCode(countryCode)
	return country?.units ?? 'metric'
}

export function getCountryNameByCode(
	code?: string | CountryCode | null,
): string | null {
	return getCountryByCode(code)?.name ?? null
}

export function getFlagByCountryCode(
	code?: string | CountryCode | null,
): string | null {
	return getCountryByCode(code)?.flag ?? null
}
